import {
  GridApi,
  GridOptions,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowGroupOpenedEvent,
  SortModelItem,
} from 'ag-grid-enterprise';
import { take } from 'rxjs';

import { Logger } from '@camelot/server';

import { doesRowMatchFilterModel } from '../helpers/filter';
import { CamGridData } from './grid-data';
import { GridMutationResult, GridMutationUpdate, IDataService, ITreeData } from './types';

type DataSourceSetupParams<T> = {
  data: T[];
  options?: Omit<GridOptions, 'treeData'> & {
    openByDefault?: boolean;
    services?: IDataService<T> | null;
    treeData?: ITreeData<T> | null;
  };
};

export class CamGridDataSource<T extends { id: string }> implements IServerSideDatasource {
  private _data = new Map<string, T>();
  private _service: IDataService<T> | null = null;
  private _getDataPath: ((data: T) => string[]) | null = null;
  private readonly _toggledKeys = new Set<string>();
  private _openByDefault = true;
  // Last child of each parent, rebuilt lazily after every store change (read by `getRowClass`).
  private _lastChildIds: Set<string> | null = null;

  constructor(private _grid: CamGridData<T>) {}

  // Single point where a row is read by a dynamic key — the only index-signature cast in the file.
  private static _read(obj: unknown, key: string): unknown {
    return (obj as Record<string, unknown> | null | undefined)?.[key];
  }

  public getRows(params: IServerSideGetRowsParams) {
    if (this._service) {
      this._getRemoteRows(params);
    } else {
      this._getLocalRows(params);
    }
  }

  get isLocal(): boolean {
    return !this._service;
  }

  public getData(): T[] {
    return [...this._data.values()];
  }

  // Called from `CamGridData.destroy()`: an empty response never reaches `_setStoreData`, so without
  // this reset the previous entity's rows stay live in `_data` and reappear.
  public clear() {
    this._data.clear();
    this._toggledKeys.clear();
    this._openByDefault = true;
    this._lastChildIds = null;
    this._service = null;
    this._getDataPath = null;
  }

  public setup(params: DataSourceSetupParams<T>) {
    if (!this._grid.api) {
      return;
    }

    this._service = params.options?.services ?? null;

    if (params.options?.treeData?.clientSide) {
      this._getDataPath = params.options.treeData.clientSide.getDataPath;
    }

    if (!this._service && params.data.length > 0) {
      this._setStoreData(params.data);
    }

    this._setupGridOptions(params);
  }

  public setData(data: T[]) {
    this._setStoreData(data);
    this._grid.api?.refreshServerSide({ purge: true });
  }

  public updateRows(mutation: GridMutationResult<T>) {
    const api = this._grid.api;
    if (!api) {
      return;
    }
    const result = this._foldUpdatesIntoAdds(mutation);
    Logger.LogDebug('[GRID-EDITABLE][CamGridDataSource] updateRows → applying backend result to the grid', result);

    // une update d'une ligne inconnue (retiree, masquee) ne doit pas la faire reapparaitre
    const updates = (result.update ?? []).filter(({ row }) => this._data.has(row.id));
    const added = result.add ?? [];
    const before = new Map(updates.map(({ row }) => [row.id, this._data.get(row.id)!]));
    const groupBefore = this._groupStates([
      ...before.keys(),
      ...added.map(row => this._parentId(row)).filter((id): id is string => !!id),
    ]);
    const reorderedLevels = this._reorderedLevels(updates);

    this._mergeUpdates(updates);
    this._lastChildIds = null;

    const addedIds = new Set(added.map(row => row.id));
    const toggledGroups = new Set(
      [...groupBefore].filter(([id, wasGroup]) => !addedIds.has(id) && wasGroup !== this._isGroup(id)).map(([id]) => id)
    );
    const routesToRefresh = new Map<string, string[] | undefined>();

    const hidden = this._applyUpdates(api, updates, before, toggledGroups, routesToRefresh);
    this._applyRemovals(api, [...(result.remove ?? []), ...hidden]);
    this._applyAdds(api, added, toggledGroups);
    this._reRenderToggled(api, toggledGroups);

    // une simple renumerotation garde les positions : seul un vrai changement d'ordre recharge le niveau
    reorderedLevels.forEach((idsBefore, key) => {
      if (!this._sameRelativeOrder(idsBefore, this._levelIds(key))) {
        routesToRefresh.set(key, this._routeOf(key));
      }
    });
    this._refreshRoutes(api, routesToRefresh);
    this._lastChildIds = null;
  }

  public isLastChild(id: string): boolean {
    if (!this._lastChildIds) {
      const lastByParent = new Map<string, string>();
      this._sortByOrder(this.getData()).forEach(row => {
        const parentId = CamGridDataSource._read(row, 'parentId');
        if (typeof parentId === 'string' && parentId) {
          lastByParent.set(parentId, row.id);
        }
      });
      this._lastChildIds = new Set(lastByParent.values());
    }
    return this._lastChildIds.has(id);
  }

  // les ajouts sont appliques apres les updates : sans ce repli, l'ajout ecraserait l'update de sa propre ligne
  private _foldUpdatesIntoAdds(result: GridMutationResult<T>): GridMutationResult<T> {
    const added = new Map((result.add ?? []).map(row => [row.id, { ...row }]));
    const folded = (result.update ?? []).filter(({ row }) => added.has(row.id));
    if (folded.length === 0) {
      return result;
    }

    folded.forEach(({ modifiedProperties, row }) => {
      const target = added.get(row.id) as Record<string, unknown>;
      (modifiedProperties?.length ? modifiedProperties : Object.keys(row)).forEach(prop => {
        target[prop] = CamGridDataSource._read(row, prop);
      });
    });
    return {
      ...result,
      add: [...added.values()],
      update: result.update?.filter(({ row }) => !added.has(row.id)),
    };
  }

  private _groupStates(ids: string[]): Map<string, boolean> {
    return new Map(ids.map(id => [id, this._isGroup(id)]));
  }

  // Levels where an `order` really changes, with their id sequence before the mutation.
  private _reorderedLevels(updates: GridMutationUpdate<T>[]): Map<string, string[]> {
    const levels = new Map<string, string[]>();
    updates.forEach(({ modifiedProperties, row }) => {
      const existing = this._data.get(row.id)!;
      const orderListed = !modifiedProperties || modifiedProperties.includes('order');
      if (!orderListed || CamGridDataSource._read(row, 'order') === CamGridDataSource._read(existing, 'order')) {
        return;
      }
      const key = this._levelKey(existing);
      if (!levels.has(key)) {
        levels.set(key, this._levelIds(key));
      }
    });
    return levels;
  }

  private _mergeUpdates(updates: GridMutationUpdate<T>[]) {
    for (const { modifiedProperties, row } of updates) {
      const existing = this._data.get(row.id);
      if (existing && modifiedProperties?.length) {
        // Merge only backend-flagged fields; everything else (incl. the tree path) keeps its value.
        const merged = { ...existing };
        modifiedProperties.forEach(prop => {
          (merged as Record<string, unknown>)[prop] = CamGridDataSource._read(row, prop);
        });
        this._data.set(row.id, merged);
      } else {
        // Clone so the store owns a mutable row (backend rows — e.g. Apollo — are frozen).
        this._data.set(row.id, { ...row });
      }
    }
  }

  // Refreshes rows in place and returns the ones that just became hidden, so they get removed.
  private _applyUpdates(
    api: GridApi<T>,
    updates: GridMutationUpdate<T>[],
    before: Map<string, T>,
    toggledGroups: Set<string>,
    routesToRefresh: Map<string, string[] | undefined>
  ): { id: string }[] {
    const hidden: { id: string }[] = [];
    for (const { row } of updates) {
      const merged = this._data.get(row.id)!;
      if (!this._grid.showHidden && CamGridDataSource._read(merged, 'hidden')) {
        hidden.push({ id: row.id });
        continue;
      }
      // Re-parent (move): the tree path changed → re-fetch both old and new parent levels.
      const oldPath = this._getDataPath?.(before.get(row.id)!);
      const newPath = this._getDataPath?.(merged);
      if (oldPath && newPath && oldPath.join('/') !== newPath.join('/')) {
        [oldPath, newPath].forEach(path => {
          const parent = path.slice(0, -1);
          routesToRefresh.set(parent.join('/'), parent.length ? parent : undefined);
        });
        continue;
      }
      if (toggledGroups.has(row.id)) {
        continue;
      }
      // `setData` re-renders every cell; a transaction `update` would leave chapter edits invisible.
      api.getRowNode(row.id)?.setData(merged);
    }
    return hidden;
  }

  // A chapter deletion spans depths and a `remove` transaction is scoped to one route.
  private _applyRemovals(api: GridApi<T>, removals: { id: string }[]) {
    const rows = removals.map(({ id }) => this._data.get(id)).filter((row): row is T => !!row);
    if (rows.length === 0) {
      return;
    }
    Logger.LogDebug('[GRID-EDITABLE][CamGridDataSource] updateRows · step: remove rows', { ids: rows.map(r => r.id) });
    rows.forEach(row => this._data.delete(row.id));
    this._groupByLevel(rows).forEach((levelRows, key) =>
      api.applyServerSideTransaction({ remove: levelRows, route: this._routeOf(key) })
    );
  }

  private _applyAdds(api: GridApi<T>, added: T[], toggledGroups: Set<string>) {
    if (added.length === 0) {
      return;
    }
    Logger.LogDebug('[GRID-EDITABLE][CamGridDataSource] updateRows · step: add rows', { ids: added.map(r => r.id) });
    // Clone so the store and the rows handed to AG Grid stay mutable (backend rows are frozen).
    const rows = added.map(row => ({ ...row }));
    rows.forEach(row => this._data.set(row.id, row));

    this._groupByLevel(this._getTopLevelRows(rows)).forEach((levelRows, key) => {
      const sorted = this._sortByOrder(levelRows);
      const route = this._routeOf(key);
      const addIndex = this._getAddIndex(sorted[0]);
      if (!route) {
        api.applyServerSideTransaction({ add: sorted, addIndex, route });
        return;
      }
      const parentKey = route[route.length - 1];
      // A parent flipping leaf→group is re-added + expanded below; its children then load lazily.
      if (toggledGroups.has(parentKey)) {
        return;
      }
      const parentNode = api.getRowNode(parentKey);
      if (parentNode?.expanded) {
        // Surgical insert under an already-loaded parent — preserves siblings' expansion state.
        api.applyServerSideTransaction({ add: sorted, addIndex, route });
      } else {
        parentNode?.setExpanded(true);
      }
    });
  }

  // Re-render flipped rows so AG Grid re-evaluates `isServerSideGroup`, which `setData` alone never does.
  private _reRenderToggled(api: GridApi<T>, toggledGroups: Set<string>) {
    toggledGroups.forEach(id => {
      const rowData = this._data.get(id);
      if (!rowData) {
        return;
      }
      const route = this._getTransactionRoute(rowData);
      api.applyServerSideTransaction({ remove: [rowData], route });
      api.applyServerSideTransaction({ add: [rowData], addIndex: this._getAddIndex(rowData), route });
      if (this._isGroup(id)) {
        api.getRowNode(id)?.setExpanded(true);
      }
    });
  }

  // Re-fetch reordered / re-parented levels; a purged level already reloads its descendants.
  private _refreshRoutes(api: GridApi<T>, routesToRefresh: Map<string, string[] | undefined>) {
    const keys = [...routesToRefresh.keys()];
    const roots = keys.filter(
      key => !keys.some(other => other !== key && (other === '' || key.startsWith(`${other}/`)))
    );
    if (roots.length === 0) {
      return;
    }
    Logger.LogDebug('[GRID-EDITABLE][CamGridDataSource] updateRows · step: re-fetch levels', { routes: roots });
    roots.forEach(key => api.refreshServerSide({ purge: true, route: routesToRefresh.get(key) }));
  }

  private _sameRelativeOrder(before: string[], after: string[]): boolean {
    const kept = new Set(before);
    const common = after.filter(id => kept.has(id));
    const commonSet = new Set(common);
    return before.filter(id => commonSet.has(id)).every((id, index) => common[index] === id);
  }

  private _groupByLevel(rows: T[]): Map<string, T[]> {
    const levels = new Map<string, T[]>();
    rows.forEach(row => {
      const key = this._levelKey(row);
      levels.set(key, [...(levels.get(key) ?? []), row]);
    });
    return levels;
  }

  private _levelIds(key: string): string[] {
    return this._sortByOrder(this.getData().filter(row => this._levelKey(row) === key)).map(row => row.id);
  }

  private _levelKey(row: T): string {
    return this._getTransactionRoute(row)?.join('/') ?? '';
  }

  private _routeOf(levelKey: string): string[] | undefined {
    return levelKey ? levelKey.split('/') : undefined;
  }

  private _getAddIndex(row: T): number | undefined {
    const parts = this._orderParts(row);
    if (!parts) {
      return undefined;
    }
    const key = this._levelKey(row);
    return this.getData().filter(other => {
      if (other.id === row.id || this._levelKey(other) !== key) {
        return false;
      }
      const otherParts = this._orderParts(other);
      return !!otherParts && this._compareOrderParts(otherParts, parts) < 0;
    }).length;
  }

  // `order` is a dotted string ("10.2.0", compared segment by segment) or a number; anything else → null.
  private _orderParts(row: T): number[] | null {
    const value = CamGridDataSource._read(row, 'order');
    const label = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '';
    const parts = label ? label.split('.').map(Number) : [];
    return parts.length > 0 && !parts.some(Number.isNaN) ? parts : null;
  }

  private _compareOrderParts(aParts: number[], bParts: number[]): number {
    const len = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < len; i++) {
      const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
      if (diff !== 0) {
        return diff;
      }
    }
    return 0;
  }

  // Tree routes only make sense while the grid actually displays a tree.
  private _getTransactionRoute(item?: T): string[] | undefined {
    if (!item || !this._getDataPath || !this._grid.treeMode) {
      return undefined;
    }
    const path = this._getDataPath(item);
    return path && path.length > 1 ? path.slice(0, -1) : undefined;
  }

  // Only the shallowest added rows go through a transaction; deeper ones surface via lazy `getRows`.
  private _getTopLevelRows(rows: T[]): T[] {
    if (rows.length <= 1 || !this._getDataPath) {
      return rows;
    }
    const minDepth = Math.min(...rows.map(r => this._getDataPath!(r).length));
    return rows.filter(r => this._getDataPath!(r).length === minDepth);
  }

  private _trackExpandedState(event: RowGroupOpenedEvent<T>) {
    const key = event.node.key;
    if (!key) {
      return;
    }
    if (event.expanded === this._openByDefault) {
      this._toggledKeys.delete(key);
    } else {
      this._toggledKeys.add(key);
    }
  }

  private _isGroupOpenByDefault(key: string | null) {
    return this._openByDefault !== (!!key && this._toggledKeys.has(key));
  }

  private _setStoreData(data: T[]) {
    this._data.clear();
    this._lastChildIds = null;
    // Clone each row: backend rows (e.g. Apollo) are frozen and AG Grid writes edits onto the row.
    data.forEach(r => this._data.set(r.id, { ...r }));
  }

  // Tree group state, read straight from the backend-computed `hasChildren` (no client aggregation).
  private _isGroup(id: string): boolean {
    return !!CamGridDataSource._read(this._data.get(id), 'hasChildren');
  }

  // Direct parent id (the segment before the row's own in the tree path), or undefined at root.
  private _parentId(row: T): string | undefined {
    const path = this._getDataPath?.(row);
    return path && path.length > 1 ? path[path.length - 2] : undefined;
  }

  private _getLocalRows(params: IServerSideGetRowsParams) {
    const groupKeys: string[] = params.request.groupKeys ?? [];
    const allRows = this._getRowsForLevel(groupKeys);

    const mergedFilterModel = {
      ...(params.request.filterModel ?? {}),
      ...(this._grid.filters.forcedFilter ?? {}),
    };
    const searchText = this._grid.filters.searchText?.trim().toLowerCase();
    const searchFields = searchText ? this._getSearchableFields() : [];
    const filteredRows = allRows.filter(
      row =>
        doesRowMatchFilterModel(row, mergedFilterModel) &&
        (!searchText || this._rowMatchesSearch(row, searchFields, searchText))
    );
    const sortedRows = this._applySortModel(filteredRows, params.request.sortModel);

    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? sortedRows.length;
    const rows = sortedRows.slice(startRow, endRow);

    params.success({ rowCount: sortedRows.length, rowData: rows });

    if (groupKeys.length === 0) {
      this._grid.filteredData$.next(sortedRows);
      this._syncNoRowsOverlay(sortedRows.length);
    }
  }

  // The server-side row model never dispatches `rowDataUpdated`, so the overlay must be driven here.
  private _syncNoRowsOverlay(rowCount: number) {
    if (rowCount === 0) {
      this._grid.api?.showNoRowsOverlay();
    } else {
      this._grid.api?.hideOverlay();
    }
  }

  // The server-side row model delegates sorting to the datasource; the sort is stable so ties keep `order`.
  private _applySortModel(rows: T[], sortModel?: SortModelItem[]): T[] {
    if (!sortModel?.length) {
      return rows;
    }
    return [...rows].sort((a, b) => {
      for (const { colId, sort } of sortModel) {
        const diff = this._compareValues(this._getFieldValue(a, colId), this._getFieldValue(b, colId));
        if (diff !== 0) {
          return sort === 'desc' ? -diff : diff;
        }
      }
      return 0;
    });
  }

  // Null/undefined always last; numbers compare numerically, dates by timestamp, the rest by collation.
  private _compareValues(a: unknown, b: unknown): number {
    if (a === b) {
      return 0;
    }
    if (a === null || a === undefined) {
      return 1;
    }
    if (b === null || b === undefined) {
      return -1;
    }
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() - b.getTime();
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }

  private _getSearchableFields(): string[] {
    const cols = this._grid.api?.getAllDisplayedColumns() ?? [];
    return cols.map(col => col.getColDef().field).filter((f): f is string => !!f);
  }

  private _rowMatchesSearch(row: T, fields: string[], search: string): boolean {
    for (const field of fields) {
      const value = this._getFieldValue(row, field);
      if (value === null || value === undefined) {
        continue;
      }
      if (String(value).toLowerCase().includes(search)) {
        return true;
      }
    }
    return false;
  }

  private _getFieldValue(row: T, field: string): unknown {
    if (!field.includes('.')) {
      return CamGridDataSource._read(row, field);
    }
    let current: unknown = row;
    for (const part of field.split('.')) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = CamGridDataSource._read(current, part);
    }
    return current;
  }

  private _getRemoteRows(params: IServerSideGetRowsParams) {
    if (this._grid.filters.forcedFilter) {
      params.request.filterModel = {
        ...params.request.filterModel,
        ...this._grid.filters.forcedFilter,
      };
    }
    this._grid.cursor.setRequestParams(params);
    // Only the root level is paginated: restoring the page on a child fetch would collapse the tree.
    const isRootLevel = !params.request.groupKeys?.length;
    this._service!.getChildren$(params)
      .pipe(take(1))
      .subscribe(rows => {
        params.success({
          rowCount: rows.totalCount,
          rowData: rows.items,
        });
        if (isRootLevel) {
          this._grid.api?.paginationGoToPage(this._grid.filters.paginationStorage ?? 0);
          this._syncNoRowsOverlay(rows.totalCount);
        }
      });
  }

  private _getRowsForLevel(groupKeys: string[]): T[] {
    const allData = this.getData();

    // `order` is the canonical sequence in both modes; only the per-level path filtering is tree-specific.
    if (!this._grid.treeMode || !this._getDataPath) {
      return this._sortByOrder(allData);
    }

    const depth = groupKeys.length + 1;
    return this._sortByOrder(
      allData.filter(row => {
        const path = this._getDataPath!(row);
        if (path.length !== depth) {
          return false;
        }
        return groupKeys.every((key, i) => path[i] === key);
      })
    );
  }

  // Rows without a usable `order` go last, so the comparator stays transitive.
  private _sortByOrder(rows: T[]): T[] {
    const orders = new Map(rows.map(row => [row, this._orderParts(row)] as const));
    return [...rows].sort((a, b) => {
      const aParts = orders.get(a);
      const bParts = orders.get(b);
      if (aParts && bParts) {
        return this._compareOrderParts(aParts, bParts);
      }
      return aParts ? -1 : bParts ? 1 : 0;
    });
  }

  private _setupGridOptions(params: DataSourceSetupParams<T>) {
    const api = this._grid.api!;

    if (params.options?.treeData) {
      this._grid.isTreeData = true;
      api.setGridOption('treeData', true);

      // Reopen tracked groups when their level reloads after a purge (cascades to children).
      this._openByDefault = params.options.openByDefault ?? true;
      api.setGridOption('isServerSideGroupOpenByDefault', params => this._isGroupOpenByDefault(params.rowNode.key));
      api.addEventListener('rowGroupOpened', (event: RowGroupOpenedEvent<T>) => this._trackExpandedState(event));

      if (params.options.treeData.clientSide) {
        api.setGridOption('isServerSideGroup', (data: T) => !!CamGridDataSource._read(data, 'hasChildren'));
        api.setGridOption('getServerSideGroupKey', (data: T) => data.id);
      } else if (params.options.treeData.serverSide) {
        if (params.options.treeData.serverSide.isServerSideGroup) {
          api.setGridOption('isServerSideGroup', params.options.treeData.serverSide.isServerSideGroup);
        }
        if (params.options.treeData.serverSide.getServerSideGroupKey) {
          api.setGridOption('getServerSideGroupKey', params.options.treeData.serverSide.getServerSideGroupKey);
        }
      }

      if (params.options.treeData.autoGroupColumnDef) {
        api.setGridOption('autoGroupColumnDef', {
          cellClass: params.options.treeData.autoGroupColumnDef.cellClass,
          cellRendererParams: {
            checkbox: false,
            innerRenderer: params.options.treeData.autoGroupColumnDef.innerRenderer,
            suppressCount: true,
          },
          editable: false,
          headerName: params.options.treeData.autoGroupColumnDef.name,
          hide: params.options.treeData.autoGroupColumnDef.hide,
          pinned: params.options.treeData.autoGroupColumnDef.pinned,
          resizable: true,
          sortable: false,
          width: params.options.treeData.autoGroupColumnDef.size ?? 240,
          ...(params.options.treeData.autoGroupColumnDef.cellComponent
            ? { cellRenderer: params.options.treeData.autoGroupColumnDef.cellComponent }
            : {}),
        });
      }
    }

    api.setGridOption('serverSideDatasource', this);
  }
}
